import { useEffect, useRef, useMemo, useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import TextField from "@/components/TextField";
import Select from "@/components/Select";
import DateSelect from "@/components/DateSelect";
import Autocomplete from "@/components/Autocomplete";
import Radio from "@/components/Radio";
import FacilityAutocomplete from "@/pages/events/_form/components/FacilityAutocomplete";
import MultiSelect from "@/components/MultiSelect";
import { QUICKSEARCH_OPTIONS_QUERY } from "@/pages/people/queries";
import { useQuery } from "@apollo/client";
import { GET_JOB_CATEGORIES_QUERY } from "../queries";
import { ALL_US_STATES } from "@/lib/usStateHelpers";
import { useParams } from "react-router-dom";
import { ServiceLevel } from "@/pages/_layouts/jobs/types";
import { validateUrl } from "../utils";
import AvatarDetails from "@/components/AvatarDetails";
import DevPrefillJobFormButton from "../dev/DevPrefillJobFormButton";
import { isDev } from "@/lib/urlUtils";

const SERVICE_LEVEL_OPTIONS = [
  { value: ServiceLevel.ASSISTED, label: ServiceLevel.ASSISTED },
  { value: ServiceLevel.ADVANCED, label: ServiceLevel.ADVANCED },
  { value: ServiceLevel.EXECUTIVE, label: ServiceLevel.EXECUTIVE },
  { value: ServiceLevel.SELF_SERVE, label: ServiceLevel.SELF_SERVE }
];

const POSITION_TYPE_OPTIONS = [
  { value: "Year Round", label: "Year Round" },
  { value: "Part Time", label: "Part Time" },
  { value: "Seasonal", label: "Seasonal" }
];

const APPLICANT_GROUP_OPTIONS = [
  { value: "PGA Professional Only", label: "PGA Professional Only" },
  {
    value: "PGA Professional and General Public",
    label: "PGA Professional and General Public"
  },
  {
    value: "PGA Golf Management University Internship",
    label: "PGA Golf Management University Internship"
  }
];

const APPLY_VIA_OPTIONS = [
  { value: "PGA.org / Email Notification", label: "PGA.org" },
  { value: "Facility Website", label: "Facility Website" }
];

interface Section {
  id: string;
  name: string;
}

const getServiceLevelOptions = (isEdit: boolean, isAdvancedOrExecutive: boolean, currentValue?: string) => {
  if (isEdit && currentValue === ServiceLevel.SELF_SERVE) {
    return SERVICE_LEVEL_OPTIONS;
  }

  if (isEdit && isAdvancedOrExecutive) {
    return SERVICE_LEVEL_OPTIONS.filter(
      ({ value }) => value !== ServiceLevel.ASSISTED && value !== ServiceLevel.SELF_SERVE
    );
  }

  return SERVICE_LEVEL_OPTIONS.filter(({ value }) => value !== ServiceLevel.SELF_SERVE);
};

export function JobDetailsStep({ form, isEdit = false }: { form: any; isEdit?: boolean }) {
  const { sectionId: urlSectionId } = useParams();

  // check url for sectionId first else show section selector
  const {
    control,
    clearErrors,
    trigger,
    setValue,
    watch,
    formState: { errors }
  } = form;

  const [selectedSectionId, setSelectedSectionId] = useState<string>(urlSectionId || "");
  const [sectionSearchTerm, setSectionSearchTerm] = useState("");
  const [isUserTyping, setIsUserTyping] = useState(false);
  const applyVia = useWatch({ control, name: "applyVia" });
  const urlInputRef = useRef<HTMLInputElement>(null);

  const { data: categoriesData } = useQuery(GET_JOB_CATEGORIES_QUERY, {
    fetchPolicy: "cache-first"
  });

  const jobCategories = useMemo(() => {
    return categoriesData?.jobPostCategories?.map((category: { name: string }) => category.name) ?? [];
  }, [categoriesData]);

  const { data: sectionData } = useQuery(QUICKSEARCH_OPTIONS_QUERY, {
    fetchPolicy: "cache-first"
  });

  const sectionOptions = useMemo(() => {
    // Filter out sections that should not be selectable in the dropdown
    const excludedSectionNames = new Set(["National Section"]);

    return (
      sectionData?.crmSections?.nodes
        ?.filter((section: Section) => !excludedSectionNames.has(section.name))
        ?.map((section: Section) => ({
          value: section.id,
          label: section.name
        })) ?? []
    );
  }, [sectionData]);

  const getSectionName = (sectionId: string) => {
    const section = sectionOptions.find((s) => s.value === sectionId);
    return section?.label || "";
  };

  const facilityOptions = ALL_US_STATES.map((state) => ({ value: state, label: state })) ?? [];

  const handleCategoryChange = (category: string) => {
    setValue("category", category);

    const currentJobTitle = watch("name");

    if (!currentJobTitle) {
      setValue("name", category);
      errors.name = undefined;
    }

    if (errors.category) {
      clearErrors("category");
    }
  };

  const resetFacilityFields = () => {
    setValue("facilityId", "");
    setValue("facility", "");
    setValue("facilityName", "");
  };

  useEffect(() => {
    if (urlSectionId) {
      setSelectedSectionId(urlSectionId);
    }
  }, [urlSectionId]);

  const serviceLevel = watch("serviceLevel");
  const isAdvancedOrExecutive = serviceLevel === "Advanced" || serviceLevel === "Executive";
  const overview = watch("overview");

  const handleServiceLevelChange = (value: string) => {
    const isAdvancedOrExecutive = value === "Advanced" || value === "Executive";

    // Save hidden fields to localStorage before switching
    const hiddenFields = {
      benefits: watch("benefits"),
      additionalIncome: watch("additionalIncome"),
      submissionDeadline: watch("submissionDeadline"),
      applicantGroup: watch("applicantGroup"),
      applyVia: watch("applyVia")
    };
    localStorage.setItem("jobPostForm_hiddenFields", JSON.stringify(hiddenFields));

    // Editing flow - preserve existing values
    if (isAdvancedOrExecutive) {
      // For Advanced/Executive, only set applyVia if it's not already set

      setValue("applyVia", "Facility Website");
      if (!watch("applicantGroup")) {
        setValue("applicantGroup", "PGA Professional and General Public");
      }
      if (!watch("submissionDeadline")) {
        setValue("submissionDeadline", { startDate: null, endDate: null });
      }
    } else {
      // For Assisted, restore hidden fields if they exist
      const savedFields = localStorage.getItem("jobPostForm_hiddenFields");
      if (savedFields) {
        const { submissionDeadline, applicantGroup, applyVia } = JSON.parse(savedFields);
        setValue("submissionDeadline", submissionDeadline);
        setValue("applicantGroup", applicantGroup);
        setValue("applyVia", applyVia);
      }
    }

    // Clear any validation errors
    clearErrors(["submissionDeadline", "applicantGroup", "applyVia", "externalJobUrl"]);
  };

  // When service level changes to Advanced/Executive, clear the job description field
  useEffect(() => {
    if (isAdvancedOrExecutive) {
      const facility = watch("facility") || "";
      const jobTitle = watch("name") || "";

      if (facility && jobTitle && (!overview || overview.trim() === "")) {
        // clear the value to allow the template to be populated
        setValue("overview", "");
      }
    }
  }, [isAdvancedOrExecutive, setValue, watch, overview]);

  return (
    <div className="space-y-6">
      <div className="text-headline-small">Enter details and location for the job.</div>

      {isDev() && (
        <DevPrefillJobFormButton
          setValue={setValue}
          clearErrors={clearErrors}
          setSelectedSectionId={setSelectedSectionId}
          jobCategories={jobCategories}
        />
      )}
      <div className="col-span-full">
        <Controller
          name="serviceLevel"
          control={control}
          rules={{ required: "Service level is required" }}
          render={({ field: { onChange, value } }) => (
            <Select
              label="Service Level"
              options={getServiceLevelOptions(isEdit, isAdvancedOrExecutive, value)}
              className="w-full"
              value={value}
              required
              error={!!errors.serviceLevel}
              errorText={errors.serviceLevel?.message as string}
              onSelect={(value) => {
                onChange(value);
                handleServiceLevelChange(value);
                if (value && errors.serviceLevel) {
                  clearErrors("serviceLevel");
                }
                if (!value) {
                  trigger("serviceLevel");
                }
              }}
            />
          )}
        />
      </div>

      <div className="col-span-full">
        <Controller
          name="category"
          control={control}
          rules={{ required: "Job category is required" }}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              label="Job Category"
              data={jobCategories.filter((jobCategory: string) =>
                jobCategory.toLowerCase().includes(value.toLowerCase())
              )}
              variant="searchableDropdown"
              query={value || ""}
              onChangeText={(e) => {
                onChange(e);
                if (e && errors.category) {
                  clearErrors("category");
                }
                if (!e) {
                  trigger("category");
                }
              }}
              renderItem={(item: string) => <div>{item}</div>}
              reset={() => onChange("")}
              onSelect={(value: string) => {
                handleCategoryChange(value);
                onChange(value);
              }}
              required
              error={!!errors.category}
              errorText={errors.category?.message as string}
              data-testid="category"
            />
          )}
        />
      </div>

      <div className="col-span-full">
        <Controller
          name="name"
          control={control}
          rules={{ required: "Job title is required" }}
          render={({ field: { onChange, value } }) => (
            <TextField
              label="Job Title"
              name="name"
              placeholder="Enter job title"
              className="w-full"
              value={value}
              onChange={(e) => {
                onChange(e);
                if ((e.target as HTMLInputElement).value && errors.name) {
                  clearErrors("name");
                }
                if (!(e.target as HTMLInputElement).value) {
                  trigger("name");
                }
              }}
              required
              error={!!errors.name}
              errorText={errors.name?.message as string}
            />
          )}
        />
      </div>

      <div data-testid="submission-deadline">
        <Controller
          name="submissionDeadline"
          control={control}
          rules={{ required: "Submission deadline is required" }}
          render={({ field }) => (
            <DateSelect
              label={isAdvancedOrExecutive ? "Submission Deadline" : "Submission Deadline *"}
              supportingText="This deadline will be set to 11:59 PM Eastern Time"
              value={field.value}
              placeholder="Submission Deadline"
              onChange={(e) => {
                field.onChange(e);
                if (e && errors.submissionDeadline) {
                  clearErrors("submissionDeadline");
                }
                if (!e) {
                  trigger("submissionDeadline");
                }
              }}
              useSingleDate
              displayFormat="MMM DD, YYYY"
              showFooter={false}
              error={!!errors.submissionDeadline}
              errorMessage={errors.submissionDeadline?.message as string}
            />
          )}
        />
      </div>

      <div className="col-span-full">
        <Controller
          name="positionType"
          control={control}
          rules={{ required: "Position type is required" }}
          render={({ field: { onChange, value } }) => (
            <Select
              label="Position Type"
              options={POSITION_TYPE_OPTIONS}
              className="w-full"
              value={value}
              required
              error={!!errors.positionType}
              errorText={errors.positionType?.message as string}
              onSelect={(value) => {
                onChange(value);
                if (value && errors.positionType) {
                  clearErrors("positionType");
                }
                if (!value) {
                  trigger("positionType");
                }
              }}
            />
          )}
        />
      </div>

      <div className="col-span-full">
        <Controller
          name="applicantGroup"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Select
              label="Applicant Group"
              options={APPLICANT_GROUP_OPTIONS}
              className="w-full"
              value={value}
              error={!!errors.applicantGroup}
              errorText={errors.applicantGroup?.message as string}
              onSelect={(value) => {
                onChange(value);
                if (value && errors.applicantGroup) {
                  clearErrors("applicantGroup");
                }
                if (!value) {
                  trigger("applicantGroup");
                }
              }}
            />
          )}
        />
      </div>

      <div className="col-span-full">
        {!urlSectionId && (
          <div className="mb-4">
            <Controller
              name="sectionId"
              control={control}
              rules={{ required: !urlSectionId && "Section is required" }}
              render={({ field }) => (
                <Autocomplete
                  label="Section"
                  name="sectionId"
                  required={!urlSectionId}
                  query={(() => {
                    if (isUserTyping) return sectionSearchTerm;
                    if (field.value) return getSectionName(field.value);
                    return sectionSearchTerm;
                  })()}
                  data={sectionOptions.filter((section) =>
                    section.label.toLowerCase().includes(sectionSearchTerm.toLowerCase())
                  )}
                  onChangeText={(string) => {
                    setIsUserTyping(true);
                    setSectionSearchTerm(string);

                    // If the input is being cleared, reset the section
                    if (!string || string.trim() === "") {
                      field.onChange("");
                      setSelectedSectionId("");
                      return;
                    }

                    if (field.value) {
                      const currentName = getSectionName(field.value);

                      if (
                        string &&
                        currentName &&
                        !currentName.toLowerCase().includes(string.toLowerCase()) &&
                        !string.toLowerCase().includes(currentName.toLowerCase())
                      ) {
                        field.onChange("");
                        setSelectedSectionId("");
                      }
                    }
                  }}
                  reset={() => {
                    field.onChange("");
                    setSelectedSectionId("");
                    setSectionSearchTerm("");
                    setIsUserTyping(false);
                  }}
                  onSelect={(value) => {
                    field.onChange(value.value);
                    setSelectedSectionId(value.value);
                    setSectionSearchTerm(value.label);
                    setIsUserTyping(false);
                    if (errors.sectionId) {
                      clearErrors("sectionId");
                    }
                    resetFacilityFields();
                  }}
                  renderItem={(data) => <AvatarDetails icon="domain" title={data.label} text="" />}
                  error={!!errors.sectionId}
                  errorText={errors.sectionId?.message as string}
                />
              )}
            />
          </div>
        )}

        <Controller
          name="facilityId"
          control={control}
          rules={{ required: "Facility is required" }}
          render={() => <FacilityAutocomplete sectionId={selectedSectionId} required={true} />}
        />
      </div>

      <div className="col-span-full">
        <p className="text-title-medium mb-2">Promote Job in Additional Sections/States</p>
        <div className="col-span-full space-y-6">
          <Controller
            name="otherSections"
            control={control}
            render={() => (
              <MultiSelect
                className="mt-3 w-full"
                label="Other Sections"
                skipShowAll={true}
                options={sectionOptions.map((option) => ({
                  value: option.label,
                  label: option.label
                }))}
                defaultLabel=""
                error={!!errors.otherSections}
                errorText={errors.otherSections?.message as string}
                selectedValues={watch("otherSections")}
                onSelect={(value, checked) => {
                  const otherSections = checked
                    ? [...watch("otherSections"), value]
                    : watch("otherSections").filter((v: string) => v !== value);
                  setValue("otherSections", otherSections);
                }}
                onClear={() => {
                  setValue("otherSections", []);
                }}
              />
            )}
          />
          <Controller
            name="otherStates"
            control={control}
            render={() => (
              <MultiSelect
                className="mt-3 w-full"
                label="Other States"
                skipShowAll={true}
                options={facilityOptions}
                defaultLabel=""
                error={!!errors.otherStates}
                errorText={errors.otherStates?.message as string}
                selectedValues={watch("otherStates")}
                onSelect={(value, checked) => {
                  const otherStates = checked
                    ? [...watch("otherStates"), value]
                    : watch("otherStates").filter((v: string) => v !== value);
                  setValue("otherStates", otherStates);
                }}
                onClear={() => {
                  setValue("otherStates", []);
                }}
              />
            )}
          />
        </div>
      </div>

      <div className="col-span-full">
        <p className="text-title-medium mb-2">Apply Via</p>
        <Controller
          control={control}
          name="applyVia"
          render={({ field }) => (
            <div className="flex flex-col" data-testid="apply-via-radio-group">
              {APPLY_VIA_OPTIONS.map((option, i) => (
                <div className={"py-3"} key={i}>
                  <Radio
                    name={`applyVia-${option.value}`}
                    label={option.label}
                    checked={field.value === option.value}
                    onChange={() => {
                      const previousValue = field.value;

                      field.onChange(option.value);
                      clearErrors("externalJobUrl");

                      // Clear the external URL when switching to PGA.org
                      if (option.value === "PGA.org / Email Notification") {
                        setValue("externalJobUrl", "");
                      }

                      if (previousValue !== "Facility Website" && option.value === "Facility Website") {
                        setTimeout(() => {
                          const urlInput = document.querySelector('input[name="externalJobUrl"]') as HTMLInputElement;
                          if (urlInput) {
                            urlInput.focus();
                          }
                        }, 0);
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        />
      </div>

      {applyVia === "Facility Website" && (
        <div className="col-span-full">
          <Controller
            name="externalJobUrl"
            control={control}
            rules={{
              required:
                applyVia === "Facility Website"
                  ? "Application URL is required when Facility Website is selected"
                  : false,
              validate: {
                validUrl: (val: string) => {
                  if (!val) return true; // Let required rule handle empty values
                  return validateUrl(val);
                }
              }
            }}
            render={({ field: { onChange, value } }) => (
              <TextField
                ref={urlInputRef}
                label="Application URL"
                placeholder="https://example.com/apply"
                className="w-full"
                value={value || ""}
                onChange={(e) => {
                  onChange(e);
                  if ((e.target as HTMLInputElement).value && errors.externalJobUrl) {
                    clearErrors("externalJobUrl");
                  }
                }}
                error={!!errors.externalJobUrl}
                errorText={errors.externalJobUrl?.message as string}
              />
            )}
          />
        </div>
      )}
    </div>
  );
}
